/**
 * EPIC 400 Phase 8B — persisted Live Alerts reconciliation.
 * Isolated helpers. WebSocket is invalidation; persisted state is authority.
 */

export type LiveAlertsReadState = 'loading' | 'ok' | 'empty' | 'unavailable' | 'partial';

export type BuyerNotificationDelivery = {
  channel?: string | null;
  lifecycle_trigger?: string | null;
  state?: string | null;
  configured?: boolean;
  buyer_copy?: string | null;
  recipient_count?: number | null;
};

export type BuyerPatternMembership = {
  min_events?: number | null;
  window_seconds?: number | null;
  observed_count?: number | null;
  triggered_at?: string | null;
  source_occurrence_ids?: string[] | null;
  source_occurrence_count?: number | null;
  source_occurrence_times?: string[] | null;
  pattern_id?: string | null;
  pattern_version?: string | number | null;
};

export type BuyerCompositeTermMembership = {
  event_id?: string | null;
  occurred_at?: string | null;
  condition_type?: string | null;
  policy_id?: string | null;
  policy_name?: string | null;
  policy_version?: string | number | null;
};

export type BuyerCompositeTerm = {
  term_id?: string | null;
  condition_type?: string | null;
  condition_label?: string | null;
  source_policy_name?: string | null;
  required_count?: number | null;
  observed_count?: number | null;
  membership?: BuyerCompositeTermMembership[] | null;
};

export type BuyerLiveOperational = {
  incident_id: string;
  condition_type?: string | null;
  incident_type?: string | null;
  policy_id?: string | null;
  area_id?: string | null;
  zone_id?: string | null;
  camera_id?: string | null;
  device_id?: string | null;
  operational_status?: string | null;
  recorded_at?: string | null;
  updated_at?: string | null;
  version?: number | null;
  organization_id?: string | null;
  site_id?: string | null;
  evidence_present?: boolean;
  alert_class?: 'Immediate' | 'Pattern' | 'Composite' | string | null;
  event_kind?: string | null;
  trigger_kind?: string | null;
  buyer_category?: string | null;
  subject_type?: string | null;
  subject_kind?: string | null;
  recurrence_copy?: string | null;
  subject?: { pattern_id?: string | null; pattern_version?: string | number | null } | null;
  direct_link?: string | null;
  pattern_membership?: BuyerPatternMembership | null;
  excluded?: boolean;
  buyer_projection_status?: string | null;
  composite_rule_id?: string | null;
  composite_rule_version?: string | number | null;
  composite_rule_name?: string | null;
  composite_operator?: 'ALL' | 'ANY' | string | null;
  composite_window_seconds?: number | null;
  composite_cooldown_seconds?: number | null;
  composite_scope_binding?: string | null;
  composite_scope_label?: string | null;
  composite_evaluated_scope_type?: string | null;
  composite_evaluated_scope_id?: string | null;
  composite_triggered_at?: string | null;
  composite_location_display?: string | null;
  composite_readable_sentence?: string | null;
  composite_terms?: BuyerCompositeTerm[] | null;
  composite_source_conditions?: string[] | null;
  severity?: {
    severity?: string | null;
    meaning?: string;
    policy_id?: string | null;
    policy_version?: string | null;
    provenance?: string | null;
  } | string | null;
  notification_delivery?: BuyerNotificationDelivery | null;
  qualified_event_count_operational?: number | null;
};

export type BuyerLiveItem = {
  operational: BuyerLiveOperational;
  claims?: Record<string, unknown>;
  claim_denials?: Record<string, string>;
};

export const PARTIAL_SOURCE_UNAVAILABLE_MESSAGE =
  'Some incident information is unavailable. This list may be incomplete.';

export type BuyerLiveResponse = {
  ok?: boolean;
  read_state?: string;
  items?: BuyerLiveItem[];
  error?: string;
  list_incomplete?: boolean;
  snapshot_complete?: boolean;
  incomplete_list_message?: string | null;
  unavailable_families?: string[];
};

export type LiveAlertsViewState = {
  readState: LiveAlertsReadState;
  items: BuyerLiveItem[];
  appliedGeneration: number;
  appliedScopeKey: string;
};

export function isEngineeringOrTransientWs(event: {
  incident_id?: string;
  alert_id?: string;
  transient?: boolean;
  engineering_signal?: boolean;
  not_incident_backed?: boolean;
}): boolean {
  if (event.transient || event.engineering_signal || event.not_incident_backed) return true;
  const id = String(event.incident_id || event.alert_id || '').trim();
  if (!id) return true;
  if (id.startsWith('alert-')) return true;
  return false;
}

export function shouldAcceptPersistedSnapshot(args: {
  generation: number;
  appliedGeneration: number;
  scopeKey: string;
  appliedScopeKey: string;
}): boolean {
  if (args.scopeKey !== args.appliedScopeKey && args.appliedScopeKey !== '') {
    // A newer scope was selected after this request started.
    if (args.generation < args.appliedGeneration) return false;
  }
  return args.generation >= args.appliedGeneration;
}

export function readStateFromResponse(
  response: BuyerLiveResponse | null,
  failed: boolean,
): LiveAlertsReadState {
  if (failed || !response || response.ok === false || response.read_state === 'unavailable') {
    return 'unavailable';
  }
  if (response.read_state === 'partial' || response.list_incomplete === true) {
    return 'partial';
  }
  if (response.read_state === 'empty' || !(response.items && response.items.length)) {
    return 'empty';
  }
  return 'ok';
}

function itemTimestamp(item: BuyerLiveItem): number {
  const raw = item.operational.updated_at || item.operational.recorded_at || '';
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function itemVersion(item: BuyerLiveItem): number {
  const version = item.operational.version;
  return typeof version === 'number' && Number.isFinite(version) ? version : 0;
}

export function mergePersistedItems(items: BuyerLiveItem[]): BuyerLiveItem[] {
  const byId = new Map<string, BuyerLiveItem>();
  for (const item of items) {
    const id = String(item?.operational?.incident_id || '').trim();
    if (!id) continue;
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, item);
      continue;
    }
    const newerVersion = itemVersion(item) > itemVersion(existing);
    const newerTime = itemTimestamp(item) >= itemTimestamp(existing);
    if (newerVersion || (itemVersion(item) === itemVersion(existing) && newerTime)) {
      byId.set(id, item);
    }
  }
  return Array.from(byId.values()).sort((a, b) => itemTimestamp(b) - itemTimestamp(a));
}

export function applyPersistedSnapshot(args: {
  generation: number;
  appliedGeneration: number;
  scopeKey: string;
  appliedScopeKey: string;
  failed: boolean;
  response: BuyerLiveResponse | null;
  previous: LiveAlertsViewState;
}): LiveAlertsViewState {
  if (!shouldAcceptPersistedSnapshot(args)) {
    return args.previous;
  }
  const readState = readStateFromResponse(args.response, args.failed);
  if (readState === 'unavailable') {
    return {
      ...args.previous,
      readState: 'unavailable',
      appliedGeneration: args.generation,
      appliedScopeKey: args.scopeKey,
    };
  }
  const items = mergePersistedItems(args.response?.items || []);
  if (readState === 'partial') {
    return {
      readState: 'partial',
      items,
      appliedGeneration: args.generation,
      appliedScopeKey: args.scopeKey,
    };
  }
  return {
    readState: items.length ? 'ok' : 'empty',
    items,
    appliedGeneration: args.generation,
    appliedScopeKey: args.scopeKey,
  };
}

export function applyLifecycleUpdate(
  items: BuyerLiveItem[],
  update: BuyerLiveItem,
): BuyerLiveItem[] {
  const id = String(update.operational.incident_id || '').trim();
  if (!id) return items;
  const next = items.filter((item) => item.operational.incident_id !== id);
  const existing = items.find((item) => item.operational.incident_id === id);
  if (existing) {
    const updateIsOlder =
      itemVersion(update) < itemVersion(existing) ||
      (itemVersion(update) === itemVersion(existing) && itemTimestamp(update) < itemTimestamp(existing));
    if (updateIsOlder) return items;
  }
  const status = String(update.operational.operational_status || '').toUpperCase();
  if (status === 'RESOLVED' || status === 'CLOSED') {
    return mergePersistedItems(next);
  }
  return mergePersistedItems([...next, update]);
}

export function newlyConfirmedIncidentIds(
  previousIds: Iterable<string>,
  nextItems: BuyerLiveItem[],
): string[] {
  const prev = new Set(Array.from(previousIds).map((id) => String(id || '').trim()).filter(Boolean));
  const confirmed: string[] = [];
  for (const item of nextItems) {
    const id = String(item?.operational?.incident_id || '').trim();
    if (!id || prev.has(id)) continue;
    confirmed.push(id);
  }
  return confirmed;
}

export function shouldPlayPersistedIncidentSound(args: {
  soundEnabled: boolean;
  invalidationType?: string;
  readState: LiveAlertsReadState;
  confirmedNewIds: string[];
}): boolean {
  if (!args.soundEnabled) return false;
  if (args.invalidationType !== 'alert.new') return false;
  if (args.readState === 'unavailable' || args.readState === 'loading') return false;
  return args.confirmedNewIds.length > 0;
}

export const EMAIL_NOT_CONFIGURED_COPY = 'Email is not configured for this incident.';
export const EMAIL_FAILED_COPY = 'Email notification failed.';

export function emailDeliveryBuyerCopy(delivery?: BuyerNotificationDelivery | null): string {
  if (!delivery || delivery.configured === false || delivery.state === 'NOT_CONFIGURED') {
    return delivery?.buyer_copy || EMAIL_NOT_CONFIGURED_COPY;
  }
  if (delivery.buyer_copy) return delivery.buyer_copy;
  if (delivery.state === 'SENT') return 'Email notification sent.';
  if (delivery.state === 'PENDING') return 'Email notification is pending.';
  if (delivery.state === 'DELIVERY_UNKNOWN') return 'Email notification status is unknown after send.';
  return EMAIL_FAILED_COPY;
}
